import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getDecisionCriteria, rankApps } from '../../api/decisions.api';
import { useMediaQuery } from 'react-responsive';
import AppSkin1 from './skins/AppSkin1';
import AppSkin2 from './skins/AppSkin2';
import AppSkin3 from './skins/AppSkin3';
import AppSkin4 from './skins/AppSkin4';
import AppSkin5 from './skins/AppSkin5';
import AppSkin6 from './skins/AppSkin6';
import AppSkin7 from './skins/AppSkin7';
import MobileSkin1 from './skins/mobile/MobileSkin1';
import { transformCriteriaData } from '../../utils';
import AppCriteriaFields from './app-components/AppCriteriaFields';
import AppCriteriaSettings from './app-components/CriteriaSettings';
import AppProductResults from './app-components/AppProductResults';
import AppInfo from './app-components/AppInfo';
import HorizontalLinearStepper from '../horizontal-linear-stepper/HorizontalLinearStepper';
import Loading from '../loading/Loading';
import { useAppConfig } from '../../context/app-configs-context';
import { allowedSkins } from '../../shared/consts';
import { AppCriteria, AppResultInfo, AppSkinProps, CriteriaConstraint, PageInfo } from '../../types/decisions';
import { useAuth } from '../../context/auth-context';
import OraChatBot from '../appContents/OraChatBot/OraChatBot';
import './application.scss';

interface ApplicationV2Props {
    criteriaData: AppCriteria,
    setCriteriaData: React.Dispatch<React.SetStateAction<AppCriteria>>,
    criteriaConstraints: {
        [key: string]: CriteriaConstraint[]
    },
    initialCriteriaData: React.MutableRefObject<AppCriteria>,
    pageInfo: PageInfo,
    setPageInfo: React.Dispatch<React.SetStateAction<PageInfo>>,
    setResults: React.Dispatch<React.SetStateAction<AppResultInfo[]>>,
    setResultLoading: React.Dispatch<React.SetStateAction<boolean>>,

    results: AppResultInfo[],
    setCriteriaConstraints: React.Dispatch<React.SetStateAction<{
        [key: string]: CriteriaConstraint[]
    }>>,
    onRerank: AppSkinProps['onRerank'],
    onRerankMany: AppSkinProps['onRerankMany'],
    onPageChange: AppSkinProps['onPageChange'],
    onSliderChange: AppSkinProps['onSliderChange'],
    onSliderBulkChange: AppSkinProps['onSliderBulkChange'],
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'],
    onCriteriaApply: AppSkinProps['onCriteriaApply'],
    onResetTradeOffs: AppSkinProps['onResetTradeOffs'],
    resultLoading: AppSkinProps['resultLoading'],
    selectedProductIds: AppSkinProps['selectedProductIds'],
    setSelectedProductIds: AppSkinProps['setSelectedProductIds'],
    showSavedSettings?: boolean;
}

const ApplicationV2 = ({
    criteriaData,
    criteriaConstraints,
    setCriteriaData,
    initialCriteriaData,
    pageInfo,
    setPageInfo,
    setResults,
    setResultLoading,
    results,
    setCriteriaConstraints,
    onRerank,
    onRerankMany,
    onPageChange,
    onSliderChange,
    onSliderBulkChange,
    onTradeoffValueChange,
    onCriteriaApply,
    onResetTradeOffs,
    resultLoading,
    selectedProductIds,
    setSelectedProductIds,
    showSavedSettings = false,
}: ApplicationV2Props) => {
    const {
        appConfigs,
        setAppConfigs,
    } = useAppConfig();
    const { id } = useParams<{
        id: string
    }>();
    const navigate = useNavigate();
    const { isAuthLoading } = useAuth();
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const patientId = searchParams.get('patientId') || '';
    const [ currentStep, setCurrentStep ] = useState(0);
    const [ criteriaLoading, setCriteriaLoading ] = useState<boolean>(true);

    const isSkinAllowed = allowedSkins.includes(criteriaData?.config.skin || 'skin3');
    const isMobile = useMediaQuery({
        query: '(max-width: 768px)',
    });

    useEffect(() => {
        if (isAuthLoading) {
            return;
        }

        if (id) {
            getDecisionCriteria(id, dataset)
            .then(response => {
                if (response.type === 'form') {
                    navigate(`/forms/${id}`);
                    return;
                }

                initialCriteriaData.current = response;
                setCriteriaData(response);
                setAppConfigs(response.view_config);
            })
            .finally(() => setCriteriaLoading(false));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ id, isAuthLoading ]);

    useEffect(() => {
        if (criteriaData.id && isSkinAllowed) {
            rankApps(
                transformCriteriaData(
                    criteriaData,
                    criteriaConstraints,
                    { isZipSingleField: appConfigs.criteriaSettings.zipcode === 'SingleField' },
                ),
                pageInfo.page_size,
                pageInfo.current_page,
                dataset,
                showSavedSettings,
                patientId,
            )
            .then(response => {
                setResults(response.results);
                setPageInfo({
                    current_page: response.current_page,
                    start: response.start,
                    end: response.end,
                    total: response.total,
                    total_pages: response.total_pages,
                    // page_size: response.page_size,
                    page_size: pageInfo.page_size,
                });
                if (showSavedSettings) {
                    setCriteriaData(prevCriteriaData => {
                        return {
                            ...prevCriteriaData,
                            fields: prevCriteriaData.fields.map((field) => {
                                const newFieldInfo = response.criteria[field.id] || {
                                    importance: field.importance, value: field.val,
                                };
                                return {
                                    ...field,
                                    ...newFieldInfo,
                                };
                            }),
                        };
                    });
                }
            })
            .finally(() => setResultLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ criteriaData.id ]);

    if (!id || criteriaData.config.skin === 'skin0') {
        return <Loading/>;
    }

    if (!criteriaLoading && !isSkinAllowed) {
        return (
            <div className="application">
                <div className="warning-banner">
                    This application is under construction. Visit <Link
                    to={`https://legacy2.sparkdit.com/decisions/view/${id}`} target="_blank">the old
                    version</Link> of
                    the application.
                </div>
            </div>
        );
    }

    const appSkins = {
        skin1: AppSkin1,
        skin2: AppSkin2,
        skin3: AppSkin3,
        skin4: AppSkin4,
        skin5: AppSkin5,
        skin6: AppSkin6,
        skin7: AppSkin7,
    };

    let CurrentSkin = appSkins[criteriaData.config.skin];

    if (isMobile) {
        CurrentSkin = MobileSkin1;
    }

    const allProps: AppSkinProps = {
        id: id as string,
        results,
        criteriaData,
        criteriaConstraints,
        setCriteriaConstraints,
        onRerank,
        onRerankMany,
        onPageChange,
        criteriaLoading,
        onSliderChange,
        onSliderBulkChange,
        onTradeoffValueChange,
        onCriteriaApply,
        onResetTradeOffs,
        fieldInitialValues: initialCriteriaData.current.fields,
        pageInfo,
        resultLoading,
        selectedProductIds,
        setSelectedProductIds,
    };

    const components = {
        Tradeoffs: <div style={{
            margin: '0 auto',
            maxWidth: 500,
        }}>
            <AppCriteriaFields {...allProps} />
        </div>,
        Settings: <AppCriteriaSettings {...allProps} criteriaFields={criteriaData.fields}/>,
        Recommend: <AppProductResults {...allProps} />,
        Infer: <AppProductResults {...allProps} />,
        ReRank: <AppProductResults {...allProps} />,
        OReRank: <AppProductResults {...allProps} />,
    };

    const allSteps = {
        TCRIR: [
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'Settings',
                component: components.Tradeoffs,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Tradeoffs',
                component: components.Tradeoffs,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
        ],
        CTRIR: [
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'Tradeoffs',
                component: components.Tradeoffs,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
        ],
        RIRAAR: [
            {
                title: 'Re-rank',
                component: components.ReRank,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'Tradeoffs',
                component: components.Tradeoffs,
            },
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
        ],
        RCIROIR: [
            {
                title: 'Re-rank',
                component: components.ReRank,
            },
            {
                title: 'Settings',
                component: components.Settings,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
            {
                title: 'Option to Re-Rank',
                component: components.OReRank,
            },
            {
                title: 'Infer',
                component: components.Infer,
            },
            {
                title: 'Recommend',
                component: components.Recommend,
            },
        ],
        All: [
            {
                title: 'All',
                component: <>
                    <CurrentSkin
                        {...allProps}
                    />
                </>,
            },
        ],
        Custom: [],
    };

    // @ts-ignore
    const journey = criteriaData.view_config.journey as 'TCRIR' | 'CTRIR' | 'RIRAAR' | 'RCIROIR' | 'All';

    const appSteps = allSteps[journey];

    return (
        <div className="application">
            {journey !== 'All' && (
                <AppInfo
                    icon={criteriaData.icon}
                    name={criteriaData.name}
                    description={criteriaData.description}
                />
            )}
            <OraChatBot pageTitle="Osteoarthritis"/>
            <HorizontalLinearStepper
                steps={appSteps}
                activeStep={currentStep}
                setActiveStep={setCurrentStep}
                showProgressbar={journey !== 'All' && appConfigs.show.progressbar}
                showActionButtons={journey !== 'All'}
            />

            {/*<div className="old-app-description">*/}
            {/*    You are still able to <Link*/}
            {/*    to={`https://legacy2.dev.sparkdit.com/decisions/view/${id}`}*/}
            {/*    target="_blank">*/}
            {/*    visit*/}
            {/*</Link> the old version.*/}
            {/*</div>*/}
        </div>
    );
};

export default ApplicationV2;